import { useEffect, useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import MetaTags from '../components/MetaTags';
import api from '../api/axios';
import './new-pages.css';

export default function GalleryPage() {
  const [site, setSite] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [photos, setPhotos] = useState([]);
  const [facebookAlbums, setFacebookAlbums] = useState([]);
  const [albums, setAlbums] = useState(['All Photos']);
  const [activeAlbum, setActiveAlbum] = useState('All Photos');
  const [photosLoading, setPhotosLoading] = useState(true);

  useEffect(() => {
    fetch('/content.json')
      .then(r => r.json())
      .then(d => { setSite(d.site); setPageLoading(false); })
      .catch(() => setPageLoading(false));

    api.get('/gallery')
      .then(res => {
        const galleries = res.data || [];
        const fbAlbums = galleries.filter(g => g.facebook_url);
        const localGalleries = galleries.filter(g => !g.facebook_url);
        const flat = localGalleries.flatMap(g =>
          (g.images || []).map(img => ({ id: `${g.id}-${img.url}`, url: img.url, caption: img.caption || '', album: g.name }))
        );
        setPhotos(flat);
        setFacebookAlbums(fbAlbums);
        const localAlbumNames = localGalleries.filter(g => (g.images || []).length > 0).map(g => g.name);
        if (localAlbumNames.length || fbAlbums.length) {
          setAlbums(['All Photos', ...localAlbumNames]);
        }
      })
      .catch(() => {})
      .finally(() => setPhotosLoading(false));
  }, []);

  const filtered = activeAlbum === 'All Photos' ? photos : photos.filter(p => p.album === activeAlbum);

  if (pageLoading) return <div className="page-loading"><div className="spinner-ring"></div></div>;
  if (!site) return null;

  return (
    <div className="App">
      <MetaTags
        title="Gallery | Melbourne Nepalese Community West"
        description="Browse photos from MNCW community events, cultural celebrations, and gatherings."
        url="https://mncw.org.au/gallery"
        image="https://mncw.org.au/assets/img/logo.jpg"
        type="website"
      />
      <Header data={site} />

      <div className="gallery-page-container">
        <div className="container">
          <div className="page-header">
            <span className="page-subtitle">Our Memories</span>
            <h1>Community Gallery</h1>
          </div>

          {/* Facebook albums section */}
          {facebookAlbums.length > 0 && (
            <div className="fb-albums-section">
              <h4 className="fb-albums-heading">
                <i className="fab fa-facebook me-2"></i>Photo Albums on Facebook
              </h4>
              <div className="fb-albums-grid">
                {facebookAlbums.map(album => (
                  <a
                    key={album.id}
                    href={album.facebook_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="fb-album-card"
                  >
                    <div className="fb-album-thumb">
                      {album.facebook_cover
                        ? <img src={album.facebook_cover} alt={album.name} />
                        : <div className="fb-album-placeholder"><i className="fab fa-facebook"></i></div>
                      }
                      <div className="fb-album-overlay"><i className="fas fa-external-link-alt"></i></div>
                    </div>
                    <div className="fb-album-info">
                      <div className="fb-album-name">{album.name}</div>
                      {album.description && <div className="fb-album-desc">{album.description}</div>}
                      <div className="fb-album-cta"><i className="fab fa-facebook me-1"></i>View on Facebook</div>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Local photo grid */}
          {photos.length > 0 && (
            <>
              {albums.length > 1 && (
                <div className="gallery-filter-tabs">
                  {albums.map(album => (
                    <button
                      key={album}
                      className={`gallery-filter-btn${activeAlbum === album ? ' active' : ''}`}
                      onClick={() => setActiveAlbum(album)}
                    >{album}</button>
                  ))}
                </div>
              )}

              {photosLoading ? (
                <div className="page-loading"><div className="spinner-ring"></div></div>
              ) : filtered.length === 0 ? null : (
                <div className="gallery-grid">
                  {filtered.map(p => (
                    <div key={p.id} className="gallery-photo">
                      <img src={p.url} alt={p.caption || 'Gallery photo'} loading="lazy" />
                      <div className="photo-overlay">
                        <i className="fas fa-search-plus"></i>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {!photosLoading && photos.length === 0 && facebookAlbums.length === 0 && (
            <div className="gallery-empty">
              <i className="fas fa-images"></i>
              <h4>Community photos will be shared here soon</h4>
              <p>Check back after events to see our community moments.</p>
            </div>
          )}
        </div>
      </div>

      <Footer data={site} />
    </div>
  );
}
